import {useQuestionsList} from "./useQuestionsList";
import {useAppDispatch, useAppSelector} from "./redux";
import {ChangeEvent, useEffect, useState} from "react";
import {IFile, IGpsrQuestion, IPoint} from "../types/gpsr-questions";
import {
    deleteFile, deleteFileThunk, getProduct, resetRiskQuestions,
    setQuestionData, updateProduct,
    uploadFiles,
} from "../store/slices/gpsrQuestionsListSlice";
import {createProduct} from "../store/slices/gpsrProductsSlice";
import {useNavigate} from "react-router-dom";
import {addNotification} from "../store/slices/notificationSlice";
import {useTranslation} from "react-i18next";
import type {GpsrProductView} from "../api/generated/models/GpsrProductView";

interface ILocalQuestionState {
    textarea: {pointID: string, text: string}[];
    files: string[];
}

interface ICurrentQuestionIDS {
    topicID: string;
    questionID: string;
    pointID: string;
}

export const useQuestions = (productID: string) => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {questionsList} = useQuestionsList();
    const dispatch = useAppDispatch();
    const {riskQuestions} = useAppSelector((state) => state.gpsrQuestionsList);

    const [isFetching, setIsFetching] = useState<boolean>(false);
    const [currentFileName, setCurrentFileName] = useState<string>('')
    const [currentOriginalFileName, setCurrentOriginalFileName] = useState<string>('')
    const [currentQuestionIDS, setCurrentQuestionIDS] = useState<ICurrentQuestionIDS>({topicID: '', questionID: '', pointID: ''})
    const [localQuestionsState, setLocalQuestionsState] = useState<ILocalQuestionState>({textarea: [], files: []});
    const [productName, setProductName] = useState<string>("");
    const [currentEditProductName, setCurrentEditProductName] = useState<string>("");
    const [isSendDataLoading, setIsSendDataLoading] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [openDeleteFileModal, setOpenDeleteFileModal] = useState<boolean>(false);
    const [questions, setQuestions] = useState<IGpsrQuestion[]>([]);

    const callBackHandler = (
        topicID: string, questionID: string, pointID: string,
        callBack: (topicID: string, questionID: string, pointID: string, point: IPoint) => void
    ) => {
        const questionsArr = [...questions];

        questionsArr.map((topic) => {
            if (topic.topicID === topicID) {
                topic.questions.map((question) => {
                    if (question.questionID === questionID) {
                        question.points.map((point) => {
                            if (point.pointID === pointID) {
                                callBack(topic.topicID, question.questionID, point.pointID, point);
                            }
                        });
                    }
                });
            }
        });
        setQuestions(questionsArr);
    };

    const textareaChange = (value: string, topicID: string, questionID: string, pointID: string) => {
        callBackHandler(
            topicID, questionID, pointID,
            (topicID, questionID, pointID, point) => {
                point.input = value;

                dispatch(
                    setQuestionData({topicID, questionID, pointID, textarea: value})
                );

                setLocalQuestionsState(prevState => ({
                    ...prevState,
                    textarea: prevState.textarea.map(item => ({
                        ...item,
                        pointID: pointID,
                        text: value
                    }))
                }));
            }
        );
    };

    const fileInputChange = async (
        event: ChangeEvent<HTMLInputElement>,
        topicID: string, questionID: string, pointID: string
    ) => {
        const files: FileList | never[] = event.target.files || [];

        if (files.length > 0) {
            try {
                const data = {files: files as FileList, topicID, questionID, pointID};
                const response = await dispatch(uploadFiles(data)).unwrap();

                if (response.status === "success") {
                    callBackHandler(
                        topicID, questionID, pointID,
                        (topicID, questionID, pointID, point
                        ) => {
                            if (response?.files && response?.files?.length > 0) {
                                const respFiles = response.files;
                                for (let i = 0; i < respFiles.length; i++) {
                                    setLocalQuestionsState(prev => ({
                                        ...prev,
                                        files: [...prev.files, respFiles[i].name],
                                    }))
                                }
                            }

                            dispatch(
                                addNotification({
                                    type: "success",
                                    title: "",
                                    message: t("gpsr:add_file_notification_success"),
                                })
                            );
                        }
                    );
                }
            } catch (error) {
                dispatch(
                    addNotification({
                        type: "error",
                        title: "",
                        message: t("gpsr:add_file_notification_error"),
                    })
                );
                console.error("Upload failed:", error);
            }
        }
    };

    const deleteFileHandle = async (topicID: string, questionID: string, pointID: string, file: IFile) => {
        const response = await dispatch(deleteFileThunk(file.name)).unwrap();

        if (response.status === "success") {
            callBackHandler(
                topicID,
                questionID,
                pointID,
                (topicID, questionID, pointID, point) => {
                    point.files = point.files.filter(
                        (pFile) => pFile.originalName !== file.originalName
                    );
                    dispatch(deleteFile({topicID, questionID, pointID, file}));
                    dispatch(
                        addNotification({
                            type: "success",
                            title: "",
                            message: t("gpsr:delete_file_notification_success"),
                        })
                    );
                }
            );
        } else {
            dispatch(
                addNotification({
                    type: "error",
                    title: "",
                    message: t("gpsr:delete_file_notification_error"),
                })
            );
        }
    };

    const confirmDeleteFileHandle = async (topicID: string, questionID: string, pointID: string, file: IFile) => {
        setCurrentFileName(file.name);
        setCurrentOriginalFileName(file.originalName);
        setCurrentQuestionIDS({topicID, questionID, pointID});
        setOpenDeleteFileModal(true);
    }

    const saveProductHandle = async () => {
        setIsSendDataLoading(true);

        const data = {name: productName, riskQuestions}

        const response = await dispatch(createProduct(data)).unwrap()

        if (response?.status === "success") {
            setIsSendDataLoading(false);
            navigate(-1);
            dispatch(
                addNotification({
                    type: "success",
                    title: "",
                    message: t("gpsr:create_product_notification_success"),
                })
            );
        } else {
            setIsSendDataLoading(false);
            navigate(-1);
            dispatch(
                addNotification({
                    type: "error",
                    title: "",
                    message: t("gpsr:create_product_notification_error"),
                })
            );
        }
    };

    const updateProductHandle = async (productID: string) => {
        setIsSendDataLoading(true);

        const data = {id: productID,  name: productName, riskQuestions}

        const response = await dispatch(updateProduct(data)).unwrap()

        if (response?.status === "success") {
            setIsSendDataLoading(false);
            navigate(-1);
            dispatch(
                addNotification({
                    type: "success",
                    title: "",
                    message: t("gpsr:update_product_notification_success"),
                })
            );
        }
    }

    const saveOrUpdateProductHandle = async (productID: string | null) => {
        if (productID) {
            updateProductHandle(productID).finally()
        } else {
            saveProductHandle().finally()
        }
    }

    const getProductID = async (productID: string) => {
        const response: GpsrProductView = await dispatch(getProduct(productID)).unwrap();
        setProductName(response?.name as string);
        setCurrentEditProductName(response?.name as string);
        return response;
    }

    const getProductHandle = () => {
        const questionsArr = [...questions];

        questionsArr.map((topic) => {
            riskQuestions.forEach((item) => {
                if (topic.topicID === item.topicId) {
                    topic.questions.map((question) => {
                        if (question.questionID === item.questionId) {
                            question.points.map((point) => {
                                if (point.pointID === item.pointId) {
                                    point.input = item.textarea ?? ""
                                    point.files = item.files
                                }
                            });
                        }
                    });
                }
            })
        });

        setQuestions(questionsArr);
    }

    const goBackHandler = () => {
        const hasLocalQuestionsStateTextareaNonEmptyValues = localQuestionsState.textarea.some(value => value.text !== "");

        if (
            productID &&
            (
                (localQuestionsState.textarea.length > 0 || hasLocalQuestionsStateTextareaNonEmptyValues) ||
                localQuestionsState.files.length > 0
            )
        ) {
            setOpen(true);
        } else if (!productID && (riskQuestions.length > 0 || productName !== "")) {
            setOpen(true);
        } else {
            dispatch(resetRiskQuestions())
            navigate(-1);
        }
    };

    useEffect(() => {
        setQuestions(questionsList)
    }, [])

    useEffect(() => {
        if (productID && riskQuestions.length > 0) getProductHandle()
    }, [riskQuestions, productID])

    return {
        questions,
        textareaChange,
        fileInputChange,
        deleteFileHandle,
        confirmDeleteFileHandle,
        currentQuestionIDS,
        saveOrUpdateProductHandle,
        getProductHandle,
        getProductID,
        productName,
        currentEditProductName,
        setProductName,
        riskQuestions,
        isSendDataLoading,
        currentFileName,
        currentOriginalFileName,
        open,
        setOpen,
        openDeleteFileModal,
        setOpenDeleteFileModal,
        goBackHandler,
    };
};
